/**
 * WalletDecisions — the "My Decisions" hub.
 *
 * Tabs:
 *   • Active   → proposals still in TRUST / UNDERSTAND / CHECK / ACT.
 *   • Completed → decisions the consumer has fully acted on.
 *
 * Empty state includes a "Send a demo proposal" button (bounded utility)
 * so the TechSprint Investment Proposal journey can be triggered without
 * an institution-side sender.
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ShieldCheck, Landmark, ArrowRight, Sparkles, RotateCcw,
  CircleCheck, CircleDashed, TimerReset,
} from "lucide-react";
import { WalletShell, NAVY, GOLD } from "@/components/wallet/WalletShell";
import {
  useWalletDecisions, DECISION_STATUS, statusLabel, zar,
  sendDemoDecision, resetDemoDecisions,
} from "@/lib/wallet-decisions";
import { loadWallet } from "@/lib/wallet-storage";

const STAGE_ORDER = [
  DECISION_STATUS.TRUST,
  DECISION_STATUS.UNDERSTAND,
  DECISION_STATUS.CHECK,
  DECISION_STATUS.ACT,
];

export default function WalletDecisions() {
  const nav = useNavigate();
  const { active, completed, loading, deviceId, refresh } = useWalletDecisions();
  const [tab, setTab] = useState("active");
  const [busy, setBusy] = useState(false);

  async function handleDemoSend() {
    if (!deviceId || busy) return;
    setBusy(true);
    try {
      const d = await sendDemoDecision(deviceId);
      await refresh();
      nav(`/wallet/decisions/${d.decision_id}`);
    } finally { setBusy(false); }
  }
  async function handleReset() {
    if (!deviceId || busy) return;
    if (!window.confirm("Reset every demo decision on this device?")) return;
    setBusy(true);
    try { await resetDemoDecisions(deviceId); await refresh(); }
    finally { setBusy(false); }
  }

  const rows = tab === "active" ? active : completed;

  return (
    <WalletShell activeTab="decisions">
      <div className="max-w-3xl mx-auto px-4 lg:px-8 py-6 lg:py-8 space-y-6" data-testid="wallet-decisions-page">
        <header>
          <div className="text-[10px] uppercase tracking-[0.24em] font-semibold" style={{ color: GOLD }}>
            My Decisions
          </div>
          <h1 className="font-display text-2xl lg:text-3xl font-semibold mt-1" style={{ color: NAVY }}>
            Every financial decision you own
          </h1>
          <p className="text-[13px] text-slate-600 mt-2 max-w-xl leading-relaxed">
            Each proposal moves through four evidence-anchored steps —
            <span className="font-semibold" style={{ color: NAVY }}> Trust</span>,{" "}
            <span className="font-semibold" style={{ color: NAVY }}>Understand</span>,{" "}
            <span className="font-semibold" style={{ color: NAVY }}>Check</span>, and{" "}
            <span className="font-semibold" style={{ color: NAVY }}>Act</span>. Nothing is decided until you say so.
          </p>
        </header>

        {/* Tabs */}
        <div className="inline-flex items-center gap-1 rounded-full bg-white border border-slate-100 p-1" data-testid="decisions-tabs">
          <TabPill
            testid="decisions-tab-active"
            active={tab === "active"}
            onClick={() => setTab("active")}
            label="Active"
            badge={active.length}
          />
          <TabPill
            testid="decisions-tab-completed"
            active={tab === "completed"}
            onClick={() => setTab("completed")}
            label="Completed"
            badge={completed.length}
          />
        </div>

        {/* Content */}
        {loading ? (
          <SkeletonRows />
        ) : rows.length === 0 ? (
          <EmptyState
            kind={tab}
            onDemoSend={handleDemoSend}
            busy={busy}
          />
        ) : (
          <div className="space-y-3">
            {rows.map(d => (
              <DecisionCard key={d.decision_id} d={d} />
            ))}
          </div>
        )}

        {/* Demo tools footer */}
        <div className="pt-4 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-500 flex-wrap gap-2" data-testid="decisions-demo-tools">
          <div>Demo tools · these seed an ABC Wealth proposal to this device.</div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDemoSend}
              disabled={busy || !deviceId}
              data-testid="decisions-demo-send"
              className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 font-medium border border-slate-200 hover:bg-slate-50 disabled:opacity-40"
              style={{ color: NAVY }}
            >
              <Sparkles className="w-3.5 h-3.5" style={{ color: GOLD }} /> Send demo proposal
            </button>
            <button
              onClick={handleReset}
              disabled={busy || !deviceId}
              data-testid="decisions-demo-reset"
              className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 font-medium border border-slate-200 hover:bg-slate-50 disabled:opacity-40"
              style={{ color: "#B91C1C" }}
            >
              <RotateCcw className="w-3.5 h-3.5" /> Reset demo
            </button>
          </div>
        </div>
      </div>
    </WalletShell>
  );
}

function TabPill({ active, onClick, label, badge, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-[12px] font-semibold transition"
      style={{
        background: active ? NAVY : "transparent",
        color: active ? "white" : "#475569",
      }}
    >
      {label}
      <span
        className="rounded-full px-2 py-[1px] text-[10px] font-mono font-semibold"
        style={{
          background: active ? "rgba(255,255,255,0.18)" : "#F1F5F9",
          color: active ? "white" : "#64748B",
        }}
      >
        {badge}
      </span>
    </button>
  );
}

function SkeletonRows() {
  return (
    <div className="space-y-3" data-testid="decisions-loading">
      {[1, 2].map(i => (
        <div key={i} className="rounded-2xl bg-white border border-slate-100 p-5 animate-pulse">
          <div className="h-4 w-40 bg-slate-100 rounded" />
          <div className="mt-3 h-3 w-64 bg-slate-100 rounded" />
          <div className="mt-4 h-8 w-full bg-slate-100 rounded" />
        </div>
      ))}
    </div>
  );
}

function EmptyState({ kind, onDemoSend, busy }) {
  const wallet = loadWallet() || {};
  const name = (wallet.name || "").split(" ")[0] || "there";
  if (kind === "completed") {
    return (
      <div className="rounded-2xl bg-white border border-slate-100 p-8 text-center" data-testid="decisions-empty-completed">
        <div className="w-11 h-11 mx-auto rounded-full flex items-center justify-center mb-3" style={{ background: "#F1F5F9" }}>
          <CircleDashed className="w-5 h-5 text-slate-400" />
        </div>
        <div className="font-display text-[15px] font-semibold" style={{ color: NAVY }}>Nothing completed yet</div>
        <div className="text-[12px] text-slate-500 mt-1">Once you finish a decision journey it will show up here.</div>
      </div>
    );
  }
  return (
    <div className="rounded-2xl bg-white border border-slate-100 p-8 text-center" data-testid="decisions-empty-active">
      <div className="w-14 h-14 mx-auto rounded-2xl flex items-center justify-center mb-3" style={{ background: `${GOLD}22` }}>
        <Sparkles className="w-6 h-6" style={{ color: NAVY }} />
      </div>
      <div className="font-display text-[16px] font-semibold" style={{ color: NAVY }}>
        {name}, no proposals waiting on you
      </div>
      <div className="text-[12.5px] text-slate-600 mt-1.5 leading-relaxed max-w-md mx-auto">
        When a participating institution sends you an investment proposal, it will appear here for your trust-first review.
      </div>
      <button
        onClick={onDemoSend}
        disabled={busy}
        data-testid="decisions-empty-demo-cta"
        className="mt-5 inline-flex items-center gap-1.5 rounded-full px-5 py-2 text-[13px] font-semibold text-white disabled:opacity-50"
        style={{ background: NAVY }}
      >
        Send demo TechSprint proposal <ArrowRight className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function DecisionCard({ d }) {
  const stageIdx = STAGE_ORDER.indexOf(d.status);
  const isDone   = d.status === DECISION_STATUS.COMPLETED;
  const isMoreInfo = d.status === DECISION_STATUS.MORE_INFO;
  const commitment = zar(d.financial_commitment_zar);
  return (
    <Link
      to={`/wallet/decisions/${d.decision_id}`}
      data-testid={`decision-card-${d.decision_id}`}
      className="block rounded-2xl bg-white border border-slate-100 hover:shadow-sm transition p-5"
    >
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${GOLD}22` }}>
          <Landmark className="w-5 h-5" style={{ color: NAVY }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div className="min-w-0">
              <div className="font-display text-[15px] font-semibold" style={{ color: NAVY }}>{d.proposal_name}</div>
              <div className="text-[12px] text-slate-600 mt-0.5 flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5" style={{ color: "#059669" }} />
                <span>{d.institution_name}</span>
                <span className="text-slate-300">·</span>
                <span>{d.adviser_name}</span>
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              <div className="text-[10px] uppercase tracking-widest text-slate-400">Commitment</div>
              <div className="font-mono font-semibold text-[13px]" style={{ color: NAVY }}>{commitment}</div>
            </div>
          </div>

          {/* Status pill for MORE_INFO */}
          {isMoreInfo && (
            <div className="mt-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10.5px] font-semibold" style={{ background: `${GOLD}22`, color: NAVY }} data-testid={`status-more-info-${d.decision_id}`}>
              <TimerReset className="w-3 h-3" /> More information requested
            </div>
          )}

          {/* Stage strip */}
          <div className="mt-3 grid grid-cols-4 gap-1.5" data-testid={`stage-strip-${d.decision_id}`}>
            {STAGE_ORDER.map((s, i) => {
              // For MORE_INFO: all 4 stages count as "engaged" (green) except the last one that stays gold
              const done    = isDone || isMoreInfo ? (i < 3) : i < stageIdx;
              const current = !isDone && !isMoreInfo && i === stageIdx;
              const moreInfoLast = isMoreInfo && i === 3;
              return (
                <div key={s} className="flex flex-col items-start">
                  <div
                    className="w-full h-1.5 rounded-full"
                    style={{
                      background: done ? "#059669" : (current || moreInfoLast) ? GOLD : "#E2E8F0",
                    }}
                  />
                  <div className="text-[10px] mt-1 font-medium" style={{ color: done || current || moreInfoLast ? NAVY : "#94A3B8" }}>
                    {statusLabel(s)}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-3 flex items-center justify-between text-[11.5px]">
            <div className="flex items-center gap-1.5 text-slate-500">
              {isDone ? (
                <><CircleCheck className="w-3.5 h-3.5" style={{ color: "#059669" }} /> Completed</>
              ) : isMoreInfo ? (
                <><TimerReset className="w-3.5 h-3.5" style={{ color: GOLD }} /> You've asked for more information before deciding.</>
              ) : (
                <><TimerReset className="w-3.5 h-3.5" style={{ color: GOLD }} /> Next: {statusLabel(d.status)}</>
              )}
            </div>
            <span className="inline-flex items-center gap-1 font-semibold" style={{ color: NAVY }}>
              Open room <ArrowRight className="w-3 h-3" />
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}
